import React, { useState, useEffect, useMemo } from 'react';
import { View, Text } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { XStack, YStack, Card, Dialog, Button } from 'tamagui';
import { MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { DefaultRootState } from '@reduxLocal/persistState';
import { SET_DRAGGED_USER } from '@reduxLocal/actions/types';
import apis from '@api/api';

interface DragDropWrapperProps {
  children: React.ReactNode;
  onDrop?: (userData: any) => void;
  onConfirmDrop?: (userData: any) => Promise<void>;
  confirmDialogTitle?: string;
  confirmDialogMessage?: string | ((userData: any) => string);
  dropZoneMessage?: string | ((userData: any) => string);
  dropZoneIcon?: string;
  disabled?: boolean;
  className?: string;
  style?: any;
}

const DragDropWrapper: React.FC<DragDropWrapperProps> = ({
  children,
  onDrop,
  onConfirmDrop,
  confirmDialogTitle = 'Confirm Action',
  confirmDialogMessage,
  dropZoneMessage,
  dropZoneIcon = 'person-add',
  disabled = false,
  className,
  style
}) => {
  const theme = themeSelector();
  const dispatch = useDispatch();

  // Get dragged user ID from Redux store
  const draggedUserId = useSelector((state: DefaultRootState) => state.UI?.draggedUser);
  const [isDragOver, setIsDragOver] = useState(false);
  const [draggedUserData, setDraggedUserData] = useState<any>(null);
  const [persistedUserData, setPersistedUserData] = useState<any>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Fetch user data when a user ID is dragged
  useEffect(() => {
    if (draggedUserId && typeof draggedUserId === 'string') {
      const fetchUserData = async () => {
        try {
          console.log('🔍 DragDropWrapper: Fetching user data for ID:', draggedUserId);
          const userData = await apis.getUserById(draggedUserId);
          console.log('✅ DragDropWrapper: User data fetched:', userData);

          if (userData && userData._id) {
            console.log('✅ DragDropWrapper: Setting draggedUserData with real user data');
            setDraggedUserData(userData);
            setPersistedUserData(userData);
          } else {
            console.warn('⚠️ DragDropWrapper: No user data found for ID:', draggedUserId);
            const fallbackData = {
              _id: draggedUserId,
              profile_information: {
                name: 'Unknown User',
                surname: '',
                avatar: { img: null }
              }
            };
            console.log('🔄 DragDropWrapper: Setting fallback user data:', fallbackData);
            setDraggedUserData(fallbackData);
            setPersistedUserData(fallbackData);
          }
        } catch (error) {
          console.error('❌ DragDropWrapper: Failed to fetch user data:', error);
          const fallbackData = {
            _id: draggedUserId,
            profile_information: {
              name: 'Unknown User',
              surname: '',
              avatar: { img: null }
            }
          };
          console.log('🔄 DragDropWrapper: Setting error fallback user data:', fallbackData);
          setDraggedUserData(fallbackData);
          setPersistedUserData(fallbackData);
        }
      };

      fetchUserData();
    } else {
      console.log('❌ DragDropWrapper: Clearing dragged user data');
      setDraggedUserData(null);
    }
  }, [draggedUserId]);

  // Handle drag over events
  const handleDragOver = (e: React.DragEvent) => {
    if (disabled) return;

    e.preventDefault();
    e.stopPropagation();
    const userDataToUse = persistedUserData || draggedUserData;
    if (userDataToUse) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    console.log('🎯 DragDropWrapper: handleDrop triggered', { disabled, draggedUserId });

    if (disabled) return;

    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const userDataToUse = persistedUserData || draggedUserData;

    console.log('🎯 DragDropWrapper: Processing drop', {
      userDataToUse,
      hasOnDrop: !!onDrop,
      hasOnConfirmDrop: !!onConfirmDrop,
      showConfirmDialog: showConfirmDialog
    });

    if (userDataToUse) {
      if (onConfirmDrop) {
        console.log('🎯 DragDropWrapper: Showing confirmation dialog');
        // Show confirmation dialog
        setShowConfirmDialog(true);
      } else if (onDrop) {
        console.log('🎯 DragDropWrapper: Calling onDrop directly');
        // Direct callback without confirmation
        onDrop(userDataToUse);
        clearUserData();
      }
    } else {
      console.error('❌ DragDropWrapper: No user data available for drop');
    }
  };

  const handleConfirmAction = async () => {
    console.log('🎯 DragDropWrapper: handleConfirmAction triggered');

    if (!onConfirmDrop) {
      console.log('❌ DragDropWrapper: No onConfirmDrop callback provided');
      return;
    }

    const userDataToUse = persistedUserData || draggedUserData;
    if (!userDataToUse) {
      console.error('❌ DragDropWrapper: No user data for confirmation');
      return;
    }

    console.log('🎯 DragDropWrapper: Calling onConfirmDrop with user data:', userDataToUse);
    setIsProcessing(true);
    setShowConfirmDialog(false);

    try {
      await onConfirmDrop(userDataToUse);

      // Show success message
      setDialogMessage(`${userDataToUse.profile_information?.name || 'User'} action completed successfully.`);
      setShowSuccessDialog(true);

      clearUserData();
    } catch (error) {
      console.error('❌ DragDropWrapper: Action failed:', error);
      setDialogMessage(`Failed to complete action for ${userDataToUse.profile_information?.name || 'user'}. Please try again.`);
      setShowErrorDialog(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelAction = () => {
    setShowConfirmDialog(false);
    clearUserData();
  };

  const handleCloseSuccess = () => {
    setShowSuccessDialog(false);
    setDialogMessage('');
  };

  const handleCloseError = () => {
    setShowErrorDialog(false);
    setDialogMessage('');
  };

  const clearUserData = () => {
    setPersistedUserData(null);
    dispatch({ type: SET_DRAGGED_USER, payload: null });
  };

  // Generate default messages
  const defaultDropZoneMessage = useMemo(() => {
    const userData = persistedUserData || draggedUserData;
    if (typeof dropZoneMessage === 'function') {
      return dropZoneMessage(userData);
    }
    const userName = userData?.profile_information?.name || 'user';
    return dropZoneMessage || `Drop ${userName} here`;
  }, [persistedUserData, draggedUserData, dropZoneMessage]);

  const defaultConfirmMessage = useMemo(() => {
    const userData = persistedUserData || draggedUserData;
    if (typeof confirmDialogMessage === 'function') {
      return confirmDialogMessage(userData);
    }
    const userName = userData?.profile_information?.name || 'user';
    return confirmDialogMessage || `Perform action for ${userName}?`;
  }, [persistedUserData, draggedUserData, confirmDialogMessage]);

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={className}
      style={{
        transition: 'all 0.2s ease-in-out',
        opacity: isDragOver ? 0.9 : 1,
        pointerEvents: draggedUserId ? 'auto' : 'none', // Enable pointer events only when dragging
        ...style
      }}
    >
      {children}

      {/* Drop Zone Indicator */}
      {isDragOver && (persistedUserData || draggedUserData) && (
        <View style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: theme === 'dark' ? 'rgba(30, 64, 175, 0.9)' : 'rgba(219, 234, 254, 0.9)',
          borderWidth: 2,
          borderColor: '#3b82f6',
          borderStyle: 'dashed',
          borderRadius: 8,
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <MaterialIcons
            name={dropZoneIcon as any}
            size={32}
            color="#3b82f6"
            style={{ marginBottom: 12 }}
          />
          <Text style={{
            color: '#3b82f6',
            fontSize: 16,
            fontWeight: '600',
            textAlign: 'center',
          }}>
            {defaultDropZoneMessage}
          </Text>
        </View>
      )}

      {/* Confirmation Dialog */}
      <Dialog modal open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <Dialog.Portal>
          <Dialog.Overlay
            key="overlay"
            animation="slow"
            opacity={0.5}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />
          <Dialog.Content
            bordered
            elevate
            key="content"
            animateOnly={['transform', 'opacity']}
            animation={[
              'quick',
              {
                opacity: {
                  overshootClamping: true,
                },
              },
            ]}
            enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
            exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
            gap="$4"
            maxWidth={400}
            padding="$6"
            width="90%"
            zIndex={200000}
          >
            <Dialog.Title size="$6" color={theme === 'dark' ? '#ffffff' : '#111827'}>
              {confirmDialogTitle}
            </Dialog.Title>
            <Dialog.Description size="$4" color={theme === 'dark' ? '#9ca3af' : '#6b7280'}>
              {defaultConfirmMessage}
            </Dialog.Description>
            <XStack gap="$3" justifyContent="flex-end" marginTop="$4">
              <Dialog.Close displayWhenAdapted asChild>
                <Button variant="outlined" onPress={handleCancelAction}>
                  Cancel
                </Button>
              </Dialog.Close>
              <Dialog.Close displayWhenAdapted asChild>
                <Button
                  theme="active"
                  onPress={handleConfirmAction}
                  disabled={isProcessing}
                  opacity={isProcessing ? 0.6 : 1}
                >
                  {isProcessing ? 'Processing...' : 'Confirm'}
                </Button>
              </Dialog.Close>
            </XStack>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>

      {/* Success Dialog */}
      <Dialog modal open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <Dialog.Portal>
          <Dialog.Overlay
            key="overlay"
            animation="slow"
            opacity={0.5}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />
          <Dialog.Content
            bordered
            elevate
            key="content"
            animateOnly={['transform', 'opacity']}
            animation={[
              'quick',
              {
                opacity: {
                  overshootClamping: true,
                },
              },
            ]}
            enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
            exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
            gap="$4"
            maxWidth={400}
            padding="$6"
            width="90%"
            zIndex={200000}
          >
            <Dialog.Title size="$6" color={theme === 'dark' ? '#ffffff' : '#111827'}>
              Success
            </Dialog.Title>
            <Dialog.Description size="$4" color={theme === 'dark' ? '#9ca3af' : '#6b7280'}>
              {dialogMessage}
            </Dialog.Description>
            <XStack gap="$3" justifyContent="flex-end" marginTop="$4">
              <Dialog.Close displayWhenAdapted asChild>
                <Button theme="active" onPress={handleCloseSuccess}>
                  OK
                </Button>
              </Dialog.Close>
            </XStack>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>

      {/* Error Dialog */}
      <Dialog modal open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <Dialog.Portal>
          <Dialog.Overlay
            key="overlay"
            animation="slow"
            opacity={0.5}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />
          <Dialog.Content
            bordered
            elevate
            key="content"
            animateOnly={['transform', 'opacity']}
            animation={[
              'quick',
              {
                opacity: {
                  overshootClamping: true,
                },
              },
            ]}
            enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
            exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
            gap="$4"
            maxWidth={400}
            padding="$6"
            width="90%"
            zIndex={200000}
          >
            <Dialog.Title size="$6" color={theme === 'dark' ? '#ffffff' : '#111827'}>
              Error
            </Dialog.Title>
            <Dialog.Description size="$4" color={theme === 'dark' ? '#9ca3af' : '#6b7280'}>
              {dialogMessage}
            </Dialog.Description>
            <XStack gap="$3" justifyContent="flex-end" marginTop="$4">
              <Dialog.Close displayWhenAdapted asChild>
                <Button theme="active" onPress={handleCloseError}>
                  OK
                </Button>
              </Dialog.Close>
            </XStack>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>
    </div>
  );
};

export default DragDropWrapper;
